import axios, { AxiosError } from 'axios';

// Import getApiBaseUrl from api.ts for consistency
import { getApiBaseUrl } from './api';

const API_BASE_URL = getApiBaseUrl();
const API_URL = `${API_BASE_URL}/api/v1/offertes`;

// Axios instance met error handling
const api = axios.create();

// Request interceptor voor authentication
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor voor error handling
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError<{ detail?: string }>) => {
    // Extract error detail from backend response
    if (error.response?.data?.detail) {
      return Promise.reject(new Error(error.response.data.detail));
    }
    return Promise.reject(error);
  }
);

export interface OfferteRegel {
  volgnummer: number;
  type_regel: 'ARBEID' | 'ONDERDEEL' | 'UITBESTEED' | 'TEKST';
  omschrijving: string;
  aantal: number;
  eenheid: string;
  prijs_per_eenheid: number;
  korting_percentage?: number;
  korting_bedrag?: number;
  totaal: number;
  actief: boolean;
  voorraad_item_id?: number;
}

export interface OfferteCalculatie {
  volgnummer: number;
  naam: string;
  omschrijving: string;
  subtotaal: number;
  actief: boolean;
  regels: OfferteRegel[];
}

export interface OfferteCreate {
  relatie_id: number;
  boot_id?: number;
  offertenummer: string;
  offerte_datum: string;
  geldig_tot: string;
  onderwerp: string;
  omschrijving?: string;
  interne_notities?: string;
  betalingstermijn: number;
  betalingsvoorwaarden?: string;
  btw_percentage: number;
  subtotaal: number;
  btw_bedrag: number;
  totaal: number;
  korting_percentage?: number;
  korting_bedrag?: number;
  uurloon: number;
  marge_uitbesteed: number;
  marge_anders: number;
  actief: boolean;
  werkorder_id?: number;
  calculaties?: OfferteCalculatie[];
  regels?: OfferteRegel[]; // Backwards compatibility field
}

export type OfferteStatus = 'CONCEPT' | 'VERZONDEN' | 'GOEDGEKEURD' | 'AFGEWEZEN' | 'GEANNULEERD' | 'VERSTUURD' | 'VERLOPEN' | 'INGETROKKEN';

export interface OfferteRelatie {
  id: number;
  bedrijfsnaam: string;
  voornaam?: string;
  tussenvoegsel?: string;
  achternaam?: string;
}

export interface Offerte extends OfferteCreate {
  id: number;
  offertenummer: string;
  status: OfferteStatus;
  goedgekeurd_op?: string;
  goedgekeurd_door?: string;
  goedgekeurd_ip?: string;
  voorwaarden_akkoord?: boolean;
  relatie?: OfferteRelatie;
}

export interface OfferteList {
  items: Offerte[];
  total: number;
}

class OffertesService {
  async getAll(params: { search?: string, status?: OfferteStatus, limit?: number, offset?: number }): Promise<OfferteList> {
    const response = await api.get(API_URL, { params });
    return response.data;
  }

  async getById(id: number): Promise<Offerte> {
    const response = await api.get(`${API_URL}/${id}`);
    return response.data;
  }

  async create(data: OfferteCreate): Promise<Offerte> {
    const response = await api.post(API_URL, data);
    return response.data;
  }

  async update(id: number, data: OfferteCreate): Promise<Offerte> {
    const response = await api.put(`${API_URL}/${id}`, data);
    return response.data;
  }

  async genereerPDF(id: number): Promise<void> {
    await api.post(`${API_URL}/${id}/genereer-pdf`);
  }

  async verzenden(id: number, email: string): Promise<{ email: string }> {
    const response = await api.post(`${API_URL}/${id}/verzenden`, { email });
    return response.data;
  }

  async updateStatus(id: number, status: OfferteStatus): Promise<Offerte> {
    const response = await api.patch(`${API_URL}/${id}/status`, { status });
    return response.data;
  }

  async delete(id: number): Promise<void> {
    await api.delete(`${API_URL}/${id}`);
  }

  async kopie(id: number): Promise<Offerte> {
    const response = await api.post(`${API_URL}/${id}/kopie`);
    return response.data;
  }

  async naarWerkorder(id: number, data: { start_datum?: string; prioriteit?: string }): Promise<{ werkorder_id: number }> {
    const response = await api.post(`${API_URL}/${id}/naar-werkorder`, data);
    return response.data;
  }
}

export const offertesService = new OffertesService();
